import React from 'react'
import { Link } from 'react-router-dom'
import Card from '@/components/ui/Card'
import {
  ATHLETIC_METRICS,
  formatMetricValue,
  getMetricPercentile,
  isEliteValue,
  type LeaderboardRanking,
  type MetricDefinition,
} from '@/lib/leaderboard-types'
import { normalizeImg } from '@/lib/images'

type Props = {
  metric: MetricDefinition
  rankings: LeaderboardRanking[]
  title?: string
  showMore?: boolean
  moreLink?: string
  highlight?: number // prospect_id to highlight
  compact?: boolean
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 text-sm font-bold text-white shadow">
        1
      </div>
    )
  }
  if (rank === 2) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-slate-300 to-slate-500 text-sm font-bold text-white shadow">
        2
      </div>
    )
  }
  if (rank === 3) {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-600 to-amber-800 text-sm font-bold text-white shadow">
        3
      </div>
    )
  }
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-stone-200 text-sm font-semibold text-stone-600">
      {rank}
    </div>
  )
}

function PercentileBar({ percentile }: { percentile: number }) {
  const getColor = () => {
    if (percentile >= 90) return 'bg-emerald-500'
    if (percentile >= 75) return 'bg-blue-500'
    if (percentile >= 50) return 'bg-amber-500'
    return 'bg-stone-400'
  }

  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-200">
      <div
        className={`h-full rounded-full transition-all ${getColor()}`}
        style={{ width: `${percentile}%` }}
      />
    </div>
  )
}

function ProspectRow({
  ranking,
  metric,
  isHighlighted,
  compact,
}: {
  ranking: LeaderboardRanking
  metric: MetricDefinition
  isHighlighted: boolean
  compact?: boolean
}) {
  const { rank, prospect, value, percentile, change } = ranking
  const isElite = isEliteValue(metric.id, value)

  const name = prospect.name || `Prospect #${prospect.prospect_id}`
  const avatarUrl = prospect.avatar_url ? normalizeImg(prospect.avatar_url) : null

  return (
    <div
      className={`flex items-center gap-3 rounded-xl p-3 transition-colors ${
        isHighlighted ? 'bg-cocoa/10 ring-2 ring-cocoa/30' : 'hover:bg-stone-50'
      } ${compact ? 'py-2' : ''}`}
    >
      <RankBadge rank={rank} />

      {/* Avatar */}
      {avatarUrl ? (
        <img src={avatarUrl} alt={name} className="h-10 w-10 rounded-full object-cover" />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-200 text-sm font-semibold text-stone-600">
          {(prospect.name?.[0] || '?').toUpperCase()}
        </div>
      )}

      {/* Name & Details */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate font-medium text-stone-900">{name}</p>
          {isElite && (
            <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-800">
              Elite
            </span>
          )}
        </div>
        {!compact && (
          <p className="truncate text-xs text-stone-500">
            {prospect.position?.toUpperCase()} · Class of {prospect.class_year}
            {prospect.state && ` · ${prospect.state}`}
          </p>
        )}
      </div>

      {/* Value & Percentile */}
      <div className="text-right">
        <p className={`font-semibold ${isElite ? 'text-emerald-700' : 'text-stone-900'}`}>
          {formatMetricValue(metric.id, value)}
        </p>
        {!compact && (
          <div className="mt-1 flex items-center gap-2">
            <div className="w-16">
              <PercentileBar percentile={percentile} />
            </div>
            <span className="text-xs text-stone-500">{percentile.toFixed(0)}%</span>
          </div>
        )}
      </div>

      {/* Change indicator */}
      {change !== undefined && change !== 0 && (
        <div className={`text-xs font-semibold ${change > 0 ? 'text-emerald-600' : 'text-brick'}`}>
          {change > 0 ? `+${change}` : change}
        </div>
      )}
    </div>
  )
}

export default function LeaderboardCard({
  metric,
  rankings,
  title,
  showMore = true,
  moreLink,
  highlight,
  compact = false,
}: Props) {
  const displayTitle = title || metric.label

  return (
    <Card className="space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-stone-900">{displayTitle}</h3>
          <p className="text-xs text-stone-500">{metric.description}</p>
        </div>
        <div className="rounded-lg bg-stone-100 px-2 py-1 text-xs font-medium text-stone-600">
          {metric.direction === 'lower_better' ? 'Lower is better' : 'Higher is better'}
        </div>
      </div>

      {rankings.length === 0 ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone-500">
          No rankings yet. Be the first to record your {metric.shortLabel}!
        </div>
      ) : (
        <div className="divide-y divide-stone-100">
          {rankings.map((ranking) => (
            <ProspectRow
              key={ranking.prospect.prospect_id}
              ranking={ranking}
              metric={metric}
              isHighlighted={highlight === ranking.prospect.prospect_id}
              compact={compact}
            />
          ))}
        </div>
      )}

      {showMore && moreLink && rankings.length > 0 && (
        <Link
          to={moreLink}
          className="block rounded-lg border border-stone-200 bg-stone-50 py-2 text-center text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100"
        >
          View Full Leaderboard
        </Link>
      )}
    </Card>
  )
}

// Compact version for sidebar/widgets
export function LeaderboardMini({
  metricId,
  rankings,
  title,
}: {
  metricId: string
  rankings: LeaderboardRanking[]
  title?: string
}) {
  const metric = ATHLETIC_METRICS[metricId]
  if (!metric) return null

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-stone-900">{title || metric.shortLabel}</h4>
        <span className="text-xs text-stone-500">Top {rankings.length}</span>
      </div>
      <div className="space-y-1">
        {rankings.slice(0, 5).map((ranking, idx) => {
          const name = ranking.prospect.name || `Prospect #${ranking.prospect.prospect_id}`
          return (
            <div
              key={ranking.prospect.prospect_id}
              className="flex items-center justify-between text-sm"
            >
              <div className="flex items-center gap-2">
                <span className="w-4 text-xs font-semibold text-stone-400">{idx + 1}</span>
                <span className="truncate text-stone-700">{name}</span>
              </div>
              <span className="font-medium text-stone-900">
                {formatMetricValue(metricId, ranking.value)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
